import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ApiError } from "../../../shared/api/client";
import {
  AppBadge,
  AppButton,
  AppField,
  AppInput,
  AppTextarea,
} from "../../../shared/components/ui";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import {
  DecisionLayout,
  DecisionPanel,
  DetailPanel,
  DetailRows,
} from "../../../shared/components/DecisionChrome";
import {
  IconCheck,
  IconError,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { getPaymentException, resolvePaymentException, searchCustomers } from "../api";
import { formatAge, formatDateTime, formatTime, formatTzs } from "../format";
import {
  EXCEPTION_REASON_LABELS,
  type CustomerSearchRow,
  type ExceptionResolveRequest,
  type PaymentException,
} from "../types";
import { reasonBadge } from "./S6ReconciliationQueue";

/**
 * S7 — Exception detail & resolve (handoff TASK-009, s7-exception-detail-*,
 * flow-29 refund path). Three audited resolution paths:
 *   match-to-contract → runs the standard arrears-first allocation;
 *   mark-duplicate    → links the original payment, never double-applies;
 *   return/refund     → M-Pesa reversal or office-cash hand-back (note REQUIRED).
 * Idempotency-Key is stable per mount, so Retry is safe. 409 = already
 * resolved. Mobile is read-only (decisions are made on desktop).
 */

type Mode = "match" | "duplicate" | "refund";

export function S7ExceptionResolve() {
  const { id = "" } = useParams();
  const exceptionId = Number(id);
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("match");
  const [search, setSearch] = useState("");
  const [candidate, setCandidate] = useState<CustomerSearchRow | null>(null);
  const [originalRef, setOriginalRef] = useState<string | null>(null); // null = use exception's
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  // Stable per mount — retrying the same resolution replays idempotently.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const query = useQuery({
    queryKey: ["payment-exception", exceptionId],
    queryFn: () => getPaymentException(exceptionId),
    retry: 1,
    staleTime: 15_000,
  });

  const candidates = useQuery({
    queryKey: ["exception-candidates", exceptionId, search.trim()],
    queryFn: () => searchCustomers(search.trim()),
    enabled: mode === "match" && search.trim().length >= 2,
    retry: 1,
    staleTime: 15_000,
  });

  const mutation = useMutation({
    mutationFn: (body: ExceptionResolveRequest) =>
      resolvePaymentException(exceptionId, body, idempotencyKey),
  });

  if (query.isPending) {
    return <PageState status="loading" />;
  }

  if (query.isError) {
    return (
      <PageState
        status="error"
        error={query.error instanceof Error ? query.error.message : "Could not load the exception"}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const exc = query.data.exception;

  // ── Success ────────────────────────────────────────────────────────────────
  if (mutation.isSuccess) {
    const resolved = mutation.data.exception;
    return (
      <Stack spacing={2} sx={{ py: 4, alignItems: "center", textAlign: "center" }}>
        <IconCheck size={40} />
        <Typography variant="h2">Exception resolved</Typography>
        <Typography color="text.secondary">
          {resolved.exception_number} — {resolutionSummary(resolved)}. Audit trail appended.
        </Typography>
        <AppButton type="button" onClick={() => navigate("/reconciliation")}>
          Back to queue
        </AppButton>
      </Stack>
    );
  }

  // ── Already resolved (from queue or 409) ──────────────────────────────────
  const apiErr = mutation.error instanceof ApiError ? mutation.error : null;
  const conflict = apiErr?.status === 409;
  const alreadyResolved = exc.status === "resolved" || conflict;

  const submit = () => {
    setFormError(null);
    if (mode === "match") {
      const contractId = candidate?.active_contract?.id;
      if (!contractId) {
        setFormError("Select a candidate with an active contract");
        return;
      }
      mutation.mutate({ action: "match", contract_id: contractId });
    } else if (mode === "duplicate") {
      const ref = (originalRef ?? exc.original_payment_number ?? "").trim();
      if (!ref) {
        setFormError("Enter the original payment reference (PAY-YYYY-####)");
        return;
      }
      mutation.mutate({ action: "mark_duplicate", original_payment_number: ref });
    } else {
      if (!note.trim()) {
        setFormError("A note is required for a return / refund");
        return;
      }
      mutation.mutate({ action: "refund", note: note.trim() });
    }
  };

  const busy = mutation.isPending;
  const refundChannel = exc.channel.toLowerCase().includes("m-pesa")
    ? "M-Pesa reversal to the payer phone"
    : "office-cash hand-back with receipt";

  return (
    <>
      <PageHeader
        title={`Exception ${exc.exception_number} — ${EXCEPTION_REASON_LABELS[exc.reason]}`}
        crumbs={[{ label: "Reconciliation", to: "/reconciliation" }, { label: "Exception" }]}
      />

      <DecisionLayout
        detail={<ExceptionDetailCard exc={exc} />}
        decision={
          <DecisionPanel title="Resolve exception" aria-label="Resolve exception">
            {alreadyResolved ? (
              <>
                <Alert severity="success" icon={<IconCheck size={14} />}>
                  {exc.status === "resolved"
                    ? `Resolved — ${resolutionSummary(exc)}.`
                    : (apiErr?.message ?? "This exception was already resolved.")}
                </Alert>
                <AppButton variant="secondary" type="button" onClick={() => navigate("/reconciliation")}>
                  Back to queue
                </AppButton>
              </>
            ) : mutation.isError ? (
              <>
                <Alert severity="error" icon={<IconError size={14} />}>
                  Resolution could not be saved — {apiErr?.message ?? "network error"}. Nothing was
                  recorded; retry, your entries are preserved.
                </Alert>
                <AppButton variant="secondary" type="button" onClick={submit} disabled={busy}>
                  {busy ? <IconSpinner size={14} /> : null} Retry
                </AppButton>
              </>
            ) : (
              <>
                {mode === "match" ? (
                  <>
                    <AppField label="Candidate matches">
                      <AppInput
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name or customer no."
                        aria-label="Search candidate customers"
                      />
                    </AppField>
                    {search.trim().length >= 2 ? (
                      candidates.isPending ? (
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <IconSpinner size={14} />
                          <Typography variant="body2" color="text.secondary">
                            Searching…
                          </Typography>
                        </Stack>
                      ) : candidates.isError ? (
                        <Alert severity="error" icon={<IconError size={14} />}>
                          Search failed — retry.
                        </Alert>
                      ) : candidates.data && candidates.data.customers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No customers match — try the customer number.
                        </Typography>
                      ) : (
                        <ToggleButtonGroup
                          orientation="vertical"
                          exclusive
                          fullWidth
                          value={candidate?.id ?? null}
                          onChange={(_e, val: number | null) => {
                            const next = candidates.data?.customers.find((c) => c.id === val) ?? null;
                            setCandidate(next);
                          }}
                          aria-label="Candidate customers"
                          sx={{
                            gap: 1,
                            "& .MuiToggleButtonGroup-grouped": {
                              border: 1,
                              borderColor: "divider",
                              borderRadius: "8px !important",
                              textTransform: "none",
                              justifyContent: "space-between",
                              px: 1.5,
                              py: 1.25,
                              minHeight: { xs: 48, sm: 44 },
                            },
                          }}
                        >
                          {candidates.data?.customers.map((c) => (
                            <ToggleButton
                              key={c.id}
                              value={c.id}
                              selected={candidate?.id === c.id}
                              sx={{
                                "&.Mui-selected": {
                                  bgcolor: "rgba(37, 99, 235, 0.08)",
                                  borderColor: "primary.main",
                                  color: "text.primary",
                                },
                              }}
                            >
                              <Stack direction="row" spacing={1} sx={{ width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                                <Stack sx={{ alignItems: "flex-start", minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {c.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                    {[
                                      c.active_contract?.contract_number ?? "no active contract",
                                      c.ward,
                                      c.active_contract && c.active_contract.arrears_cents > 0
                                        ? `arrears ${formatTzs(c.active_contract.arrears_cents)}`
                                        : "no arrears",
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </Typography>
                                </Stack>
                                {candidate?.id === c.id ? <IconCheck size={14} /> : null}
                              </Stack>
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      )
                    ) : null}

                    <AppButton type="button" onClick={submit} disabled={busy || !candidate}>
                      {busy ? <IconSpinner size={14} /> : null} Match to contract — apply allocation
                    </AppButton>
                    <AppButton
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setMode("duplicate");
                        setFormError(null);
                      }}
                      disabled={busy}
                    >
                      Mark duplicate — link original
                    </AppButton>
                    <AppButton
                      variant="secondary"
                      type="button"
                      className="warning"
                      onClick={() => {
                        setMode("refund");
                        setFormError(null);
                      }}
                      disabled={busy}
                    >
                      Return / refund (note required)
                    </AppButton>
                    <Typography variant="body2" color="text.secondary">
                      Matching applies the standard allocation flow: arrears first, then activation
                      days.
                    </Typography>
                  </>
                ) : mode === "duplicate" ? (
                  <>
                    <AppField label="Original payment (PAY-YYYY-####)">
                      <AppInput
                        value={originalRef ?? exc.original_payment_number ?? ""}
                        onChange={(e) => setOriginalRef(e.target.value)}
                        placeholder="PAY-2026-3112"
                        aria-label="Original payment reference"
                      />
                    </AppField>
                    <Typography variant="body2" color="text.secondary">
                      The exception is linked to the original payment — the amount is NOT applied
                      twice.
                    </Typography>
                    <AppButton type="button" onClick={submit} disabled={busy}>
                      {busy ? <IconSpinner size={14} /> : null} Mark duplicate — link original
                    </AppButton>
                    <AppButton
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setMode("match");
                        setFormError(null);
                      }}
                      disabled={busy}
                    >
                      Back to match
                    </AppButton>
                  </>
                ) : (
                  <>
                    <AppField label="Refund note (required)">
                      <AppTextarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Why is this payment being returned?"
                      />
                    </AppField>
                    <Typography variant="body2" color="text.secondary">
                      Channel: {refundChannel}.
                    </Typography>
                    <AppButton
                      type="button"
                      className="warning"
                      onClick={submit}
                      disabled={busy}
                    >
                      {busy ? <IconSpinner size={14} /> : null} Return / refund
                    </AppButton>
                    <AppButton
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setMode("match");
                        setFormError(null);
                      }}
                      disabled={busy}
                    >
                      Back to match
                    </AppButton>
                  </>
                )}

                {formError ? (
                  <Alert severity="warning" icon={<IconWarning size={14} />}>
                    {formError}
                  </Alert>
                ) : null}
              </>
            )}
          </DecisionPanel>
        }
      />
    </>
  );
}

function resolutionSummary(exc: PaymentException): string {
  if (exc.resolution === "matched") return "matched to contract and allocated";
  if (exc.resolution === "marked_duplicate") return "marked as duplicate and linked to the original";
  if (exc.resolution === "refunded") return `refunded${exc.resolution_note ? ` — ${exc.resolution_note}` : ""}`;
  return "resolved";
}

function ExceptionDetailCard({ exc }: { exc: PaymentException }) {
  const rows: { label: string; value: ReactNode }[] = [
    { label: "Received", value: `${formatDateTime(exc.received_at)} · ${exc.channel}` },
    { label: "Reference", value: exc.reference },
    { label: "Amount", value: formatTzs(exc.amount_cents) },
    { label: "Payer phone", value: exc.payer_phone ?? "—" },
    { label: "Failure", value: exc.failure_detail ?? EXCEPTION_REASON_LABELS[exc.reason] },
    { label: "Age", value: formatAge(exc.received_at) },
  ];
  if (exc.original_payment_number) {
    rows.push({
      label: "Original payment",
      value: <AppBadge tone="info">{exc.original_payment_number}</AppBadge>,
    });
  }

  return (
    <DetailPanel
      title={`Exception ${exc.exception_number}`}
      badge={reasonBadge(exc.reason)}
      aria-label="Exception detail"
    >
      <DetailRows rows={rows} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Audit trail
      </Typography>
      <Stack spacing={0.75}>
        {exc.audit.map((a, i) => (
          <Stack key={i} direction="row" spacing={1.5} sx={{ alignItems: "baseline" }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56, flexShrink: 0 }}>
              {formatTime(a.at)}
            </Typography>
            <Typography variant="body2">{a.message}</Typography>
          </Stack>
        ))}
        {exc.audit.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No audit entries.
          </Typography>
        ) : null}
      </Stack>
    </DetailPanel>
  );
}
